import React, { useEffect, useState } from "react";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import { DA_Button } from "@danishagro/shared/src/components/atoms/Button/Button.component";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import { DA_DropdownArrow } from "@danishagro/shared/src/components/atoms/DropdownArrow/DropdownArrow.component";
import {
    useDynamicOverlay,
    DA_DynamicOverlayWrapper,
} from "@danishagro/shared/src/hooks/dynamicOverlay/useDynamicOverlay.hook";
import { DA_SelectOption } from "@danishagro/shared/src/components/atoms/Select/Select.props";
import { DA_InputWrapper } from "@danishagro/shared/src/components/atoms/InputWrapper/InputWrapper.component";
import { DA_Icon, DA_IconNames } from "@danishagro/shared/src/components/atoms/Icon/Icon.component";
import S from "./MobileDeliveryAddressSelect.module.scss";

export interface MobileDeliveryAddressSelectProps {
    options: DA_SelectOption[];
    value: string;
    onChange: (option: DA_SelectOption) => void;
    placeholder?: string;
    isDisabled?: boolean;
    onAddNewAddress: (eventOrCallback?: React.MouseEvent<HTMLButtonElement> | (() => void)) => void;
    bottomSheetStyle?: boolean;
}

export const MobileDeliveryAddressSelect: React.FC<MobileDeliveryAddressSelectProps> = ({
    options,
    value,
    onChange,
    placeholder,
    isDisabled,
    onAddNewAddress,
    bottomSheetStyle,
}) => {
    const [selectedOption, setSelectedOption] = useState<DA_SelectOption | null>(
        options.find((option) => option.value === value) || null
    );

    const { getDictionaryString } = useTranslations();
    const { showOverlay, closeOverlay } = useDynamicOverlay();

    useEffect(() => {
        setSelectedOption(options.find((option) => option.value === value) || null);
    }, [value, options]);

    // Create a component that manages its own local state
    const OverlayContent = () => {
        const [localTempSelection, setLocalTempSelection] = useState<DA_SelectOption | null>(
            selectedOption
        );

        const handleLocalOptionClick = (option: DA_SelectOption) => {
            setLocalTempSelection(option);
        };

        const handleLocalSave = () => {
            if (localTempSelection) {
                onChange(localTempSelection);
                setSelectedOption(localTempSelection);
            }
            closeOverlay();
        };

        const handleLocalCancel = () => {
            closeOverlay();
        };

        const handleLocalAddNewAddress = () => {
            // Create a success callback that closes the delivery address select overlay
            const onSuccess = () => {
                closeOverlay(); // This will close the delivery address select overlay
            };

            // Pass the success callback to the hook
            // The hook should call this when the address is successfully saved
            onAddNewAddress(onSuccess);
        };

        return (
            <DA_DynamicOverlayWrapper
                content={
                    <div>
                        <div className={S.optionsContainer}>
                            <div className={S.introText}>
                                {getDictionaryString(
                                    "siloAndHouses.mobileDeliveryAddressHelperText"
                                )}
                            </div>
                            {options.map((option) => (
                                <button
                                    onClick={() => handleLocalOptionClick(option)}
                                    key={String(option.value)}
                                    className={cn(
                                        S.deliveryOptionButton,
                                        localTempSelection &&
                                            localTempSelection.value === option.value &&
                                            S.selected
                                    )}
                                >
                                    {(() => {
                                        // Split the label into street and city/zip parts
                                        const labelStr = String(option.label);
                                        const parts = labelStr.split(", ");
                                        if (parts.length >= 2) {
                                            // Find the part that starts with 4 digits (likely zip code)
                                            const zipPartIndex = parts.findIndex((part) =>
                                                /^\d{4}/.test(part.trim())
                                            );
                                            if (zipPartIndex !== -1) {
                                                const streetPart = parts
                                                    .slice(0, zipPartIndex)
                                                    .join(", ");
                                                const cityZipPart = parts
                                                    .slice(zipPartIndex)
                                                    .join(", ");

                                                // Only show two lines if both parts have content
                                                if (streetPart.trim() && cityZipPart.trim()) {
                                                    return (
                                                        <div className={S.addressTwoLines}>
                                                            <span className={S.streetLine}>
                                                                {streetPart},
                                                            </span>
                                                            <span className={S.cityZipLine}>
                                                                {cityZipPart}
                                                            </span>
                                                        </div>
                                                    );
                                                }
                                            }
                                        }
                                        // Fallback to single line if we can't parse it properly
                                        return labelStr;
                                    })()}
                                </button>
                            ))}
                        </div>
                    </div>
                }
                footer={
                    <>
                        <div className={S.stickyAddButtonContainer}>
                            <button
                                onClick={handleLocalAddNewAddress}
                                className={S.addNewAddressButton}
                            >
                                <DA_Icon name={DA_IconNames.Add} />{" "}
                                {getDictionaryString("siloAndHouses.addManualAddress")}
                            </button>
                        </div>
                        <div className={S.mobileDeliveryFooter}>
                            <DA_Button
                                onClick={handleLocalCancel}
                                isGhost={true}
                                title={getDictionaryString("close")}
                            />
                            <DA_Button
                                onClick={handleLocalSave}
                                title={getDictionaryString("FilterSaveButton")}
                            />
                        </div>
                    </>
                }
            />
        );
    };

    const handleOpenOverlay = () => {
        if (isDisabled) return;

        showOverlay({
            id: `mobile-delivery-address-select-${Date.now()}`,
            title: `${getDictionaryString("siloAndHouses.addManualAddress")}`,
            content: <OverlayContent />,
        });
    };

    const displayText = selectedOption
        ? selectedOption.label
        : placeholder || getDictionaryString("siloAndHouses.addManualAddress");

    return (
        <DA_InputWrapper
            label={getDictionaryString("siloAndHouses.deliveryAddress")}
            forId="deliver-addresses-mobile"
            className={bottomSheetStyle && S.bottomSheetStyle}
            overlayHidden
        >
            <button
                className={cn(S.mobileDeliveryButton, isDisabled && S.disabled)}
                onClick={handleOpenOverlay}
                disabled={isDisabled}
                id="deliver-addresses-mobile"
            >
                <div className={S.mobileDeliveryButtonContent}>
                    <span className={cn(S.mobileDeliveryLabel, !selectedOption && S.placeholder)}>
                        {displayText}
                    </span>
                </div>
                {bottomSheetStyle ? (
                    <DA_Icon
                        name={DA_IconNames.ChevronRight}
                        className={S.bottomSheetStyleRightIcon}
                    />
                ) : (
                    <div className={S.mobileDeliveryArrow}>
                        <DA_DropdownArrow small />
                    </div>
                )}
            </button>
        </DA_InputWrapper>
    );
};
